/**
 * Schema-driven provision form — renders fields from agent definition + pack overlay.
 */

import React, { useRef } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { SetupFieldSchema } from '@/types/aiStudioAgent';
import { hasSetupFieldValue } from './setupFieldValues';

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';

interface InstanceSetupSchemaFormProps {
  fields: SetupFieldSchema[];
  values: Record<string, unknown>;
  saving: boolean;
  uploadingFieldKey: string | null;
  onChange: (key: string, value: unknown) => void;
  onUploadFile: (fieldKey: string, file: File) => void;
  onNext: () => void;
}

export const InstanceSetupSchemaForm: React.FC<InstanceSetupSchemaFormProps> = ({
  fields,
  values,
  saving,
  uploadingFieldKey,
  onChange,
  onUploadFile,
  onNext,
}) => {
  const { t } = useLanguage();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const canProceed = fields.every((field) => {
    if (!field.required) return true;
    return hasSetupFieldValue(field.type, field.key, values[field.key]);
  });

  const renderControl = (field: SetupFieldSchema) => {
    const value = values[field.key];
    const disabled = saving || uploadingFieldKey === field.key;
    const inputId = `setup-field-${field.key}`;

    if (field.type === 'file') {
      const uploaded = hasSetupFieldValue('file', field.key, value);
      return (
        <div className="flex flex-col gap-2">
          <input
            ref={(el) => {
              fileInputRefs.current[field.key] = el;
            }}
            id={inputId}
            type="file"
            accept=".json,.csv,application/json,text/csv"
            className="hidden"
            disabled={disabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUploadFile(field.key, file);
              event.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileInputRefs.current[field.key]?.click()}
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {uploadingFieldKey === field.key
                ? t('aiStudio.instanceSetup.uploading')
                : t('aiStudio.instanceSetup.uploadButton')}
            </button>
            {uploaded && (
              <span className="text-sm text-[var(--color-success)]">
                {t('aiStudio.instanceSetup.fieldFileUploaded')}
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instanceSetup.acceptFormats')}
          </p>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          id={inputId}
          className={FIELD_CLASS}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      );
    }

    if (field.type === 'secret') {
      return (
        <input
          id={inputId}
          type="password"
          autoComplete="off"
          className={FIELD_CLASS}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      );
    }

    if (field.type === 'number') {
      return (
        <input
          id={inputId}
          type="number"
          className={FIELD_CLASS}
          value={typeof value === 'number' ? value : typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => {
            const parsed = event.target.value === '' ? undefined : Number(event.target.value);
            onChange(field.key, parsed);
          }}
        />
      );
    }

    if (field.type === 'boolean') {
      return (
        <label className="flex items-center gap-2 text-base text-[var(--color-surface-text)]">
          <input
            id={inputId}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={(event) => onChange(field.key, event.target.checked)}
          />
          {field.label}
        </label>
      );
    }

    if (field.type === 'enum' && field.enumValues?.length) {
      return (
        <select
          id={inputId}
          className={FIELD_CLASS}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(field.key, event.target.value)}
        >
          <option value="">—</option>
          {field.enumValues.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'json') {
      const text =
        typeof value === 'string'
          ? value
          : value !== undefined
            ? JSON.stringify(value, null, 2)
            : '';
      return (
        <textarea
          id={inputId}
          className={`${FIELD_CLASS} font-mono text-sm`}
          rows={4}
          value={text}
          disabled={disabled}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      );
    }

    return (
      <input
        id={inputId}
        type="text"
        className={FIELD_CLASS}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(field.key, event.target.value)}
      />
    );
  };

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.instanceSetup.schemaFormTitle')}
      </h2>
      <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instanceSetup.schemaFormHint')}
      </p>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {fields.map((field) => {
          const inputId = `setup-field-${field.key}`;
          return (
            <div
              key={field.key}
              className={field.type === 'textarea' || field.type === 'json' || field.type === 'file' ? 'md:col-span-2' : ''}
            >
              {field.type !== 'boolean' && (
                <label className={LABEL_CLASS} htmlFor={inputId}>
                  {field.label}
                  {field.required && (
                    <span className="ml-1 text-[var(--color-error)]" aria-hidden>
                      *
                    </span>
                  )}
                </label>
              )}
              {field.description && field.type !== 'boolean' && (
                <p className="mb-1 text-sm text-[var(--color-surface-text-secondary)] whitespace-pre-line">
                  {field.description}
                </p>
              )}
              {renderControl(field)}
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <button
          type="button"
          disabled={saving || !canProceed}
          onClick={() => void onNext()}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
        >
          {saving ? t('aiStudio.common.saving') : t('aiStudio.instanceSetup.next')}
        </button>
      </div>
    </section>
  );
};
